import { collateMapping, produceKeyedMapping } from '@percept/utils'

import { MultiChartData } from '@percept/mui/charts'
import { BasePerformanceSegmentation, DataWarehouseQueryResponse, DataWarehouseQueryResponseRow } from '../hooks'
import { ChartBuilderDimensionType, ChartBuilderState } from './typings'
import { providerLabelMap, reportProvidersV2 } from '@percept/constants'
import { ReportProviderV2, TimeseriesGranularity } from '@percept/types'
import { every, sortBy, sum, uniq } from 'lodash-es'
import { AppTheme, DateRangeValue } from '@percept/mui'
import { getMetricLabel } from '../lib'
import { ScaleOrdinal } from 'd3'
import { differenceInDays } from 'date-fns'
import { TimelineDatum } from '@percept/app-components'
import { ChartData } from '@percept/mui/charts'


export const getBaseSegmentations = (
  dimensionType: ChartBuilderDimensionType | null
): BasePerformanceSegmentation[] => {
  if( dimensionType === 'organisation' ){
    return ['ORG_UNIT']
  }
  if( dimensionType === 'provider' ){
    return ['PROVIDER']
  }
  return ['NONE']
}

const orderedGranularities: TimeseriesGranularity[] = [
  'DAY',
  'WEEK',
  'MONTH',
  'QUARTER',
  'FINANCIAL_YEAR',
  'CALENDAR_YEAR',
]

export const getNearestNeighbourGranularity = (
  current: TimeseriesGranularity,
  options: TimeseriesGranularity[],
): TimeseriesGranularity => {
  if( options.includes(current) ){
    return current
  }
  if( options.length === 1 ){
    return options[0]
  }
  const currentIndex = orderedGranularities.indexOf(current)
  const optionIndices = options.map( g => orderedGranularities.indexOf(g) )
  if( every(optionIndices, i => i > currentIndex ) ){
    return orderedGranularities[Math.min(...optionIndices)]
  }
  if( every(optionIndices, i => i < currentIndex ) ){
    return orderedGranularities[Math.max(...optionIndices)]
  }
  // Shouldn't get here in practice, return a sane default anyway
  return options[options.length - 1]
}

export const getGranularityOptions = ([start, end]: DateRangeValue): TimeseriesGranularity[] => {
  if( !start || !end ){
    return []
  }
  const dayDelta = Math.abs(differenceInDays(start, end))
  if( dayDelta <= 7 ){
    return ['DAY']
  }
  if( dayDelta <= 31 ){
    return ['DAY', 'WEEK']
  }
  if( dayDelta <= 92 ){
    return ['WEEK', 'MONTH']
  }
  if( dayDelta <= 365 ){
    return ['WEEK', 'MONTH', 'QUARTER']
  }
  if( dayDelta <= (365 * 2) ){
    return ['MONTH', 'QUARTER', 'CALENDAR_YEAR', 'FINANCIAL_YEAR']
  }
  return ['QUARTER', 'CALENDAR_YEAR', 'FINANCIAL_YEAR']
}

const dimensionKeys: Record<ChartBuilderDimensionType, string> = {
  organisation: 'org_unit_name',
  provider: 'provider',
  'marketing-funnel': 'marketing_funnel',
  'campaign-objective': 'campaign_objective',
}

const getDimensionValue = (
  row: DataWarehouseQueryResponseRow,
  dimensionType: ChartBuilderDimensionType
): string => (
  row.dimensions[dimensionKeys[dimensionType]]
)

const getDimensionLabel = (
  row: DataWarehouseQueryResponseRow,
  dimensionType: ChartBuilderDimensionType
): string => {
  const value = row.dimensions[dimensionKeys[dimensionType]]
  if( dimensionType === 'provider' ){
    return providerLabelMap[value as ReportProviderV2]
  }
  return value
}

export type MetricLabelStrategy = null | 'merge' | 'override'

export const getColourScale = ({
  dimensionType,
  dimensionValues,
  appTheme,
}: {
  dimensionType: ChartBuilderDimensionType | null
  dimensionValues: string[]
  appTheme: AppTheme
}): ScaleOrdinal<string, string> => {
  return appTheme.chart.getOrdinalColourScale(
    dimensionType === 'provider' ? reportProvidersV2 : dimensionValues.slice().sort()
  )
}


export const warehouseResponseToChartData = ({
  queryResponse,
  state,
  metric,
  appTheme,
  colorTransform,
  getSVGPathProps,
  metricLabelStrategy = null
}: {
  queryResponse: DataWarehouseQueryResponse
  state: ChartBuilderState
  metric: string
  appTheme: AppTheme
  colorTransform?: ((color: string) => string) | null
  getSVGPathProps?: (key: string) => MultiChartData['svgPathProps']
  metricLabelStrategy?: MetricLabelStrategy
}): MultiChartData[] => {
  const dimensionType = state.dimensionType || 'provider'
  const rowsByDimension = collateMapping(queryResponse.rows, r => getDimensionValue(r, dimensionType))
  const colourScale = getColourScale({
    dimensionType: state.dimensionType,
    dimensionValues: Object.keys(rowsByDimension),
    appTheme,
  })
  const labels = uniq(queryResponse.rows.map( r => r.dimensions.period_start))
  const datasets: MultiChartData[] = Object.entries(rowsByDimension).reduce( (acc, [key, rows]) => {
    const dimensionLabel = state.dimensionType && getDimensionLabel(rows[0], dimensionType)
    if( dimensionLabel === 'Unknown' ){
      return acc
    }
    let color = colourScale(key)
    if( colorTransform ){
      color = colorTransform(color)
    }
    const svgPathProps = getSVGPathProps && getSVGPathProps(key)
    const metricLabel = getMetricLabel(metric)
    let datasetLabel = dimensionLabel || metricLabel
    if( metricLabelStrategy && !!state.dimensionType ){
      const metricLabel = getMetricLabel(metric)
      datasetLabel = (
        metricLabelStrategy === 'merge' ?
          `${metricLabel} (${dimensionLabel})` :
          metricLabel
      )
    }
    const rowsByLabel = produceKeyedMapping(rows, r => r.dimensions.period_start)
    const data: ChartData<TimelineDatum> = labels.map( label => {
      const start = new Date(label).getTime()
      const matchedRow = rowsByLabel[label]
      return {
        key: start,
        label: start,
        value: matchedRow ? matchedRow.metrics[metric] : null,
        color,
        start,
        end: start,
      }
    })
    if( sum(data.map( d => Number(d.value))) === 0 ){
      return acc
    }
    acc.push({
      key,
      title: dimensionLabel || '',
      label: datasetLabel,
      color,
      data,
      svgPathProps,
    })
    return acc
  }, [] as MultiChartData[])
  const sortIteratee: string | ((dataset: MultiChartData) => number) = (
    dimensionType === 'organisation' ? 'label' : d => reportProvidersV2.indexOf(d.key as ReportProviderV2)
  )
  return sortBy(datasets, sortIteratee)
}